import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { CheckpointOverviewPageComponent } from '@pages/checkpoint-overview-page/checkpoint-overview-page.component';
import { CheckpointDetailPageComponent } from '@pages/checkpoint-detail-page/checkpoint-detail-page.component';
import { UserDetailPageComponent } from '@pages/user-detail-page/user-detail-page.component';
import {
  CheckpointGroupOverviewPageComponent,
} from '@pages/checkpoint-group-overview-page/checkpoint-group-overview-page.component';
import {
  CheckpointGroupDetailPageComponent,
} from '@pages/checkpoint-group-detail-page/checkpoint-group-detail-page.component';
import { TenantDetailPageComponent } from '@pages/tenant-detail-page/tenant-detail-page.component';
import { BuildingOverviewPageComponent } from '@pages/building-overview-page/building-overview-page.component';
import { BuildingDetailPageComponent } from '@pages/building-detail-page/building-detail-page.component';
import { LoginPageComponent } from '@pages/login-page/login-page.component';
import { ForcePasswordChangePageComponent } from '@pages/force-password-change-page/force-password-change-page.component';
import { AuthGuard } from '@guards/auth.guard';
import { NoAuthGuard } from '@guards/no-auth.guard';
import { TgaTypeOverviewPageComponent } from '@pages/tga-type-overview-page/tga-type-overview-page.component';
import { TgaTypeDetailPageComponent } from '@pages/tga-type-detail-page/tga-type-detail-page.component';
import {
  InternalTgaOverviewPageComponent,
} from '@pages/internal-tga-overview-page/internal-tga-overview-page.component';
import {
  BuildingEquipmentDetailPageComponent,
} from '@pages/building-equipment-detail-page/building-equipment-detail-page.component';
import { InspectionOverviewPageComponent } from '@pages/inspection-overview-page/inspection-overview-page.component';
import {
  InspectionDashboardPageComponent,
} from '@pages/inspection-dashboard-page/inspection-dashboard-page.component';
import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { LogoutPageComponent } from '@pages/logout-page/logout-page.component';
import { DebugPageComponent } from '@pages/debug-page/debug-page.component';
import { PendingChangesGuard } from '@guards/pending-changes.guard';
import { InspectionDetailPageV2Component } from '@pages/inspection-detail-page-v2/inspection-detail-page-v2.component';
import { SettingsPageComponent } from '@pages/settings-page/settings-page.component';
import { ConfiguredActionsPageComponent } from '@pages/configured-actions-page/configured-actions-page.component';
import { TagOverviewPageComponent } from '@pages/tag-overview-page/tag-overview-page.component';
import { TagDetailPageComponent } from '@pages/tag-detail-page/tag-detail-page.component';
import {
  CustomFieldSplitViewComponent,
} from '@pages/custom-fields-settings-page/custom-field-split-view/custom-field-split-view.component';
import {
  CustomFieldCollectionSplitViewComponent,
} from '@pages/custom-fields-settings-page/custom-field-collection-split-view/custom-field-collection-split-view.component';
import { MapOverviewComponent } from '@pages/map-overview/map-overview.component';
import { PortfolioOverviewPageComponent } from '@pages/portfolio-overview-page/portfolio-overview-page.component';
import { ArchivePageComponent } from '@pages/archive-page/archive-page.component';
import {
  BuildingRepairTaskOverviewPageComponent,
} from '@pages/building-repair-task-overview-page/building-repair-task-overview-page.component';
import {
  InternalMaintenanceTimerOverviewPageComponent,
} from '@pages/internal-maintenance-timer-overview-page/internal-maintenance-timer-overview-page.component';
import { UserDashboardPageComponent } from '@pages/user-dashboard-page/user-dashboard-page.component';
import { TenantDashboardPageComponent } from '@pages/tenant-dashboard-page/tenant-dashboard-page.component';
import { TemplateOverviewPageComponent } from '@pages/template-overview-page/template-overview-page.component';
import { TemplateDetailPageComponent } from '@pages/template-detail-page/template-detail-page.component';
import { SharePdfPageComponent } from '@pages/share-pdf-page/share-pdf-page.component';
import { ShareProtocolPdfPageComponent } from '@pages/share-protocol-pdf-page/share-protocol-pdf-page.component';
import { EmployeeStatisticsPageComponent } from '@pages/employee-statistics-page/employee-statistics-page.component';
import { TenantStatisticsPageComponent } from '@pages/tenant-statistics-page/tenant-statistics-page.component';
import {
  NotificationOverviewPageComponent,
} from '@pages/notification-overview-page/notification-overview-page.component';
import {
  ExternalBuildingOverviewPageComponent,
} from '@pages/external-building-overview-page/external-building-overview-page.component';
import {
  ExternalInspectionOverviewPageComponent,
} from '@pages/external-inspection-overview-page/external-inspection-overview-page.component';
import {
  ExternalMaintenanceTimerOverviewPageComponent,
} from '@pages/external-maintenance-timer-overview-page/external-maintenance-timer-overview-page.component';
import { AdminUserOverviewPageComponent } from '@pages/admin-user-overview-page/admin-user-overview-page.component';
import { EmailOverviewPageComponent } from '@pages/email-overview-page/email-overview-page.component';
import { EmailDetailPageComponent } from '@pages/email-detail-page/email-detail-page.component';
import {
  MaintenanceTimerCalendarPageComponent,
} from '@pages/maintenance-timer-calendar-page/maintenance-timer-calendar-page.component';
import {
  MaintenanceTimerDashboardPageComponent,
} from '@pages/maintenance-timer-dashboard-page/maintenance-timer-dashboard-page.component';
import {
  MaintenanceTimerStatisticsPageComponent,
} from '@pages/maintenance-timer-statistics-page/maintenance-timer-statistics-page.component';
import {
  InspectionEntryStatisticPageComponent,
} from '@pages/inspection-entry-statistic-page/inspection-entry-statistic-page.component';
import { DefectReportPageComponent } from '@pages/defect-report-page/defect-report-page.component';
import { LandingPageComponent } from '@pages/landing-page/landing-page.component';
import { BuildingScanDetailPageComponent } from '@pages/building-scan-detail-page/building-scan-detail-page.component';
import { ShareLinkPageComponent } from '@pages/share-link-page/share-link-page.component';
import {
  BuildingScanShareLinkPageComponent,
} from '@pages/share-link-page/building-scan-share-link-page/building-scan-share-link-page.component';
import {
  ExternalTgaOverviewPageComponent,
} from '@pages/external-tga-overview-page/external-tga-overview-page.component';
import {
  ExternalTenantOverviewPageComponent,
} from '@pages/external-tenant-overview-page/external-tenant-overview-page.component';
import { TenantUserOverviewPageComponent } from '@pages/tenant-user-overview-page/tenant-user-overview-page.component';
import {
  InternalTenantOverviewPageComponent,
} from '@pages/internal-tenant-overview-page/internal-tenant-overview-page.component';
import { permissionGuard, tenantTypeGuard, tgaPermissionGuard } from '@guards/permission.guard';
import { UserDetailDto } from '@artemis-software/wr-api';
import { repairTasksVisibleGuard } from '@guards/repair-tasks-visible.guard';
import { CorporateGroupOverviewPageComponent } from '@pages/corporate-group-overview-page/corporate-group-overview-page.component';
import { CorporateGroupDetailPageComponent } from '@pages/corporate-group-detail-page/corporate-group-detail-page.component';
import { TenantRoleOverviewPageComponent } from '@pages/tenant-role-overview-page/tenant-role-overview-page.component';
import { TenantRoleDetailPageComponent } from '@pages/tenant-role-detail-page/tenant-role-detail-page.component';
import { TeamOverviewPageComponent } from '@pages/team-overview-page/team-overview-page.component';
import { TeamDetailPageComponent } from '@pages/team-detail-page/team-detail-page.component';
import { RegionOverviewPageComponent } from '@pages/region-overview-page/region-overview-page.component';
import { RegionDetailPageComponent } from '@pages/region-detail-page/region-detail-page.component';
import {
  BuildingWorkItemOverviewPageComponent,
} from '@pages/building-work-item-overview-page/building-work-item-overview-page.component';
import {
  AttachmentFolderDetailPageComponent,
} from '@pages/attachment-folder-detail-page/attachment-folder-detail-page.component';
import {
  ContractorTenantOverviewPageComponent,
} from '@pages/contractor-tenant-overview-page/contractor-tenant-overview-page.component';
import {
  ContractorBuildingOverviewPageComponent,
} from '@pages/contractor-building-overview-page/contractor-building-overview-page.component';
import {
  ContractorRepairTaskOverviewPageComponent,
} from '@pages/contractor-repair-task-overview-page/contractor-repair-task-overview-page.component';
import {
  ContractorScopeConfigurationPageComponent,
} from '@pages/contractor-scope-configuration-page/contractor-scope-configuration-page.component';
import {
  ContractorAttachmentFolderConfigurationPageComponent,
} from '@pages/contractor-attachment-folder-configuration-page/contractor-attachment-folder-configuration-page.component';
import {
  ContractorBuildingAssignmentPageComponent,
} from '@pages/contractor-building-assignment-page/contractor-building-assignment-page.component';
import { TeamDashboardPageComponent } from '@pages/team-dashboard-page/team-dashboard-page.component';
import { RegionDashboardPageComponent } from '@pages/region-dashboard-page/region-dashboard-page.component';
import { SensorBlockDetailPageComponent } from '@pages/sensor-block-detail-page/sensor-block-detail-page.component';
import { LabelOverviewPageComponent } from '@pages/label-overview-page/label-overview-page.component';
import { LabelDetailPageComponent } from '@pages/label-detail-page/label-detail-page.component';
import { FlatDetailPageComponent } from '@pages/flat-detail-page/flat-detail-page.component';
import { QrCodeGeneratePageComponent } from '@pages/qr-code-generate-page/qr-code-generate-page.component';
import {
  QrCodeBuildingOverviewPageComponent,
} from '@pages/qr-code-building-overview-page/qr-code-building-overview-page.component';
import { QrCodePinLoginComponent } from '@pages/qr-code-pin-login/qr-code-pin-login.component';
import { QrAccessGuard } from '@guards/qr-access.guard';
import { QrCodeTgaOverviewPageComponent } from '@pages/qr-code-tga-overview-page/qr-code-tga-overview-page.component';
import {
  QrCodeMaintenanceTimerPageComponent,
} from '@pages/qr-code-maintenance-timer-page/qr-code-maintenance-timer-page.component';
import {
  QrCodeMaintenanceTimerFinishPageComponent,
} from '@pages/qr-code-maintenance-timer-finish-page/qr-code-maintenance-timer-finish-page.component';
import { QrCodeTgaDetailPageComponent } from '@pages/qr-code-tga-detail-page/qr-code-tga-detail-page.component';
import { QrCodeRepairTaskPageComponent } from '@pages/qr-code-repair-task-page/qr-code-repair-task-page.component';
import { QrExternalBuildingsPageComponent } from '@pages/qr-external-buildings-page/qr-external-buildings-page.component';
import { TgaStatisticPageComponent } from '@pages/tga-statistic-page/tga-statistic-page.component';
import {
  MaintenanceTimerHistoryOverviewPageComponent,
} from '@pages/maintenance-timer-history-overview-page/maintenance-timer-history-overview-page.component';
import {
  MaintenanceTimerHistoryDetailPageComponent,
} from '@pages/maintenance-timer-history-detail-page/maintenance-timer-history-detail-page.component';
import {
  BuildingPointCloudScanPageComponent
} from '@pages/building-point-cloud-scan-page/building-point-cloud-scan-page.component';
import {
  BuildingApplicationOverviewComponent
} from '@pages/building-application/building-application-overview/building-application-overview.component';
import {
  BuildingApplicationDetailComponent
} from '@pages/building-application/building-application-detail/building-application-detail.component';
import {
  BuildingApplicationAdminOverviewComponent
} from '@pages/building-application/building-application-admin-overview/building-application-admin-overview.component';
import {
  BuildingApplicationAdminDetailComponent
} from '@pages/building-application/building-application-admin-detail/building-application-admin-detail.component';
import {
  BuildingPointCloudScanShareLinkPageComponent
} from '@pages/share-link-page/building-point-cloud-scan-share-link-page/building-point-cloud-scan-share-link-page.component';
import {
  ProtocolTypeOverviewPageComponent,
} from '@pages/protocol-type-overview-page/protocol-type-overview-page.component';
import { ProtocolTypeDetailPageComponent, } from '@pages/protocol-type-detail-page/protocol-type-detail-page.component';
import { ProtocolOverviewPageComponent, } from '@pages/protocol-overview-page/protocol-overview-page.component';
import { ProtocolCreationPageComponent, } from '@pages/protocol-creation-page/protocol-creation-page.component';
import { ProtocolDetailPageComponent, } from '@pages/protocol-detail-page/protocol-detail-page.component';

// TODO: WR-1338
const routes: Routes = [
  {
    path: 'login',
    component: LoginPageComponent,
    canActivate: [NoAuthGuard],
  },
  {
    path: 'force-password-change',
    component: ForcePasswordChangePageComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'logout',
    component: LogoutPageComponent,
  },
  {
    path: 'pdf/:id',
    component: SharePdfPageComponent,
  },
  {
    path: 'protocol-share/:id',
    component: ShareProtocolPdfPageComponent,
  },
  {
    path: 'dashboard',
    children: [
      {
        path: 'tenant',
        component: TenantDashboardPageComponent,
        canActivate: [AuthGuard, permissionGuard('viewExternalTenantDashboard')],
      },
      {
        path: 'user',
        component: UserDashboardPageComponent,
        canActivate: [AuthGuard, permissionGuard('viewUserDashboard')],
      },
      {
        path: 'region',
        component: RegionDashboardPageComponent,
        canActivate: [AuthGuard, permissionGuard('viewTeams')],
      },
      {
        path: 'team',
        component: TeamDashboardPageComponent,
        canActivate: [AuthGuard, permissionGuard('viewTeams')],
      },
      {
        path: 'maintenanceTimers',
        component: MaintenanceTimerDashboardPageComponent,
        canActivate: [AuthGuard, permissionGuard('viewMaintenanceTimerDashboard')],
      },
      {
        path: 'inspections',
        component: InspectionDashboardPageComponent,
        canActivate: [AuthGuard, permissionGuard('viewInternalInspections')],
      },
      {
        path: 'building-new',
        loadChildren: () =>
          import('@pages/building-dashboard-new/building-dashboard-new.module').then((m) => m.BuildingDashboardNewModule),
      }
    ],
  },
  {
    path: 'sensorBlock/:id',
    component: SensorBlockDetailPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewSensorDashboards')],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'checkpoint/:id',
    component: CheckpointDetailPageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'checkpoints',
    canActivate: [AuthGuard, permissionGuard('viewCheckpoints')],
    component: CheckpointOverviewPageComponent,
  },
  {
    path: 'user/:id',
    component: UserDetailPageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'adminUsers',
    component: AdminUserOverviewPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewAllTenantUsers')],
  },
  {
    path: 'tenantUsers',
    component: TenantUserOverviewPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewUsers')],
  },
  {
    path: 'tenantRoles',
    component: TenantRoleOverviewPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewTenantRoles')],
  },
  {
    path: 'tenantRole/:id',
    component: TenantRoleDetailPageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'regions',
    component: RegionOverviewPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewRegions')],
  },
  {
    path: 'region/:id',
    component: RegionDetailPageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'contractorBuildings',
    component: ContractorBuildingOverviewPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewContractorBuildings')],
  },
  {
    path: 'contractor/repairTasks',
    component: ContractorRepairTaskOverviewPageComponent,
    canActivate: [AuthGuard, tenantTypeGuard(UserDetailDto.TenantTypeEnum.Contractor)],
  },
  {
    path: 'contractorScopes',
    component: ContractorScopeConfigurationPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewContractorScopes')],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'contractorAttachmentFolders',
    component: ContractorAttachmentFolderConfigurationPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewContractorAttachmentFolderConfig')],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'teams',
    component: TeamOverviewPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewTeams')],
  },
  {
    path: 'team/:id',
    component: TeamDetailPageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'checkpointGroup/:id',
    component: CheckpointGroupDetailPageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'checkpointGroups',
    canActivate: [AuthGuard, permissionGuard('viewCheckpointGroups')],
    component: CheckpointGroupOverviewPageComponent,
  },
  {
    path: 'tenant/:id',
    component: TenantDetailPageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'externalTenants',
    component: ExternalTenantOverviewPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewExternalTenants')],
  },
  {
    path: 'internalTenants',
    component: InternalTenantOverviewPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewInternalTenants')],
  },
  {
    path: 'corporate-groups',
    component: CorporateGroupOverviewPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewCorporateGroup')],
  },
  {
    path: 'corporate-group/:id',
    component: CorporateGroupDetailPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewCorporateGroup')],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'contractorTenants',
    component: ContractorTenantOverviewPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewContractors')],
  },
  {
    path: 'contractorBuildingAssignment',
    component: ContractorBuildingAssignmentPageComponent,
    canActivate: [AuthGuard, permissionGuard('editContractorBuildingAssignments')],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'buildings',
    component: BuildingOverviewPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewInternalBuildings')],
  },
  {
    path: 'qr/:shortCode',
    component: QrCodePinLoginComponent,
  },
  {
    path: 'building-qr/:shortCode',
    canActivate: [QrAccessGuard],
    children: [
      { path: '', component: QrCodeBuildingOverviewPageComponent },
      { path: 'tga', component: QrCodeTgaOverviewPageComponent },
      { path: 'tga/:buildingEquipmentId', component: QrCodeTgaDetailPageComponent },
      { path: 'maintenance', component: QrCodeMaintenanceTimerPageComponent },
      { path: 'maintenance/finish/:timerId', component: QrCodeMaintenanceTimerFinishPageComponent },
      { path: 'repairTasks', component: QrCodeRepairTaskPageComponent },
      { path: 'external', component: QrExternalBuildingsPageComponent },
      { path: 'external/building/:buildingId', redirectTo: 'building/:buildingId' },
      {
        path: 'building/:buildingId',
        children: [
          { path: '', component: QrCodeBuildingOverviewPageComponent },
          { path: 'tga', component: QrCodeTgaOverviewPageComponent },
          { path: 'tga/:buildingEquipmentId', component: QrCodeTgaDetailPageComponent },
          { path: 'maintenance', component: QrCodeMaintenanceTimerPageComponent },
          { path: 'maintenance/finish/:timerId', component: QrCodeMaintenanceTimerFinishPageComponent },
          { path: 'repairTasks', component: QrCodeRepairTaskPageComponent },
        ],
      },
    ],
  },
  {
    path: 'attachmentFolder/:id',
    component: AttachmentFolderDetailPageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'buildingWorkItems',
    component: BuildingWorkItemOverviewPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewInternalBuildingWorkItems')],
  },
  {
    path: 'building/:id',
    component: BuildingDetailPageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'map',
    component: MapOverviewComponent,
    canActivate: [AuthGuard, permissionGuard('viewBuildingMap')],
  },
  {
    path: 'portfolioOverview',
    component: PortfolioOverviewPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewSensors')],
  },
  {
    path: 'qrCodes',
    component: QrCodeGeneratePageComponent,
    canActivate: [AuthGuard, permissionGuard('viewQrCodeGeneration')],
  },
  {
    path: 'buildingApplications',
    component: BuildingApplicationOverviewComponent,
    canActivate: [AuthGuard, permissionGuard('viewBuildingApplications')],
  },
  {
    path: 'buildingApplication/:id',
    component: BuildingApplicationDetailComponent,
    canActivate: [AuthGuard, permissionGuard('viewBuildingApplications')],
  },
  {
    path: 'buildingApplicationsAdmin',
    component: BuildingApplicationAdminOverviewComponent,
    canActivate: [AuthGuard, permissionGuard('viewBuildingApplicationsAdmin')],
  },
  {
    path: 'buildingApplicationAdmin/:id',
    component: BuildingApplicationAdminDetailComponent,
    canActivate: [AuthGuard, permissionGuard('viewBuildingApplicationsAdmin')],
  },
  {
    path: 'equipments',
    canActivate: [AuthGuard, permissionGuard('viewTgaTypes')],
    component: TgaTypeOverviewPageComponent,
  },
  {
    path: 'equipment/:id',
    component: TgaTypeDetailPageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'buildingEquipments',
    canActivate: [AuthGuard, permissionGuard('viewInternalTgas')],
    component: InternalTgaOverviewPageComponent,
  },
  {
    path: 'buildingEquipment/:id',
    component: BuildingEquipmentDetailPageComponent,
    canActivate: [AuthGuard, tgaPermissionGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'buildingEquipmentStatistic',
    component: TgaStatisticPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewInternalTgas')],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'buildingEquipmentExternalStatistic',
    component: TgaStatisticPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewExternalTgas')],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'maintenanceTimers',
    children: [
      {
        path: '',
        redirectTo: 'overview',
        pathMatch: 'full',
      },
      {
        path: 'overview',
        canActivate: [AuthGuard, permissionGuard('viewInternalMaintenanceTimers')],
        component: InternalMaintenanceTimerOverviewPageComponent,
      },
      {
        path: 'calendar',
        canActivate: [AuthGuard, permissionGuard('viewMaintenanceTimerCalendar')],
        component: MaintenanceTimerCalendarPageComponent,
      },
      {
        path: 'historyOverview',
        canActivate: [AuthGuard, permissionGuard('viewInternalMaintenanceTimers')],
        component: MaintenanceTimerHistoryOverviewPageComponent,
      },
      {
        path: 'historyDetail/:id',
        canActivate: [AuthGuard, permissionGuard('viewInternalMaintenanceTimers')],
        component: MaintenanceTimerHistoryDetailPageComponent,
      },
    ],
  },
  {
    path: 'inspections',
    component: InspectionOverviewPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewInternalInspections')],
  },
  {
    path: 'external',
    canActivate: [AuthGuard],
    children: [
      {
        path: 'inspections',
        component: ExternalInspectionOverviewPageComponent,
        canActivate: [AuthGuard, permissionGuard('viewExternalInspections')],
      },
      {
        path: 'maintenanceTimers',
        canActivate: [AuthGuard, permissionGuard('viewExternalMaintenanceTimers')],
        component: ExternalMaintenanceTimerOverviewPageComponent,
      },
      {
        path: 'buildingEquipments',
        canActivate: [AuthGuard, permissionGuard('viewExternalTgas')],
        component: ExternalTgaOverviewPageComponent,
      },
      {
        path: 'buildings',
        component: ExternalBuildingOverviewPageComponent,
        canActivate: [AuthGuard, permissionGuard('viewExternalBuildings')],
      },
      {
        path: 'maintenanceTimers/historyOverview',
        canActivate: [AuthGuard, permissionGuard('viewExternalMaintenanceTimers')],
        component: MaintenanceTimerHistoryOverviewPageComponent,
      },
      {
        path: 'maintenanceTimers/historyDetail/:id',
        canActivate: [AuthGuard, permissionGuard('viewExternalMaintenanceTimers')],
        component: MaintenanceTimerHistoryDetailPageComponent,
      },
    ],
  },
  {
    path: 'inspection/:id',
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'general',
      },
      {
        path: ':inspectionTab',
        component: InspectionDetailPageV2Component,
        canActivate: [AuthGuard],
        canDeactivate: [PendingChangesGuard],
      },
    ],
  },
  {
    path: 'configured-actions',
    component: ConfiguredActionsPageComponent,
    canActivate: [AuthGuard, permissionGuard('editTenant')],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'tags',
    component: TagOverviewPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewTags')],
  },
  {
    path: 'tag/:id',
    component: TagDetailPageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'building-repair-tasks/:id',
    component: BuildingRepairTaskOverviewPageComponent,
    canActivate: [AuthGuard, repairTasksVisibleGuard],
  },
  {
    path: 'templates',
    canActivate: [AuthGuard, permissionGuard('viewTemplates')],
    component: TemplateOverviewPageComponent,
  },
  {
    path: 'template/:id',
    component: TemplateDetailPageComponent,
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'debug',
    canActivate: [AuthGuard, permissionGuard('viewDebugInfo')],
    component: DebugPageComponent,
  },
  {
    path: 'emails',
    component: EmailOverviewPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewEmails')],
  },
  {
    path: 'email/:id',
    component: EmailDetailPageComponent,
  },
  {
    path: 'archive',
    component: ArchivePageComponent,
    canActivate: [AuthGuard, permissionGuard('viewArchive')],
  },
  {
    path: 'statistics',
    canActivate: [AuthGuard],
    children: [
      { path: '', redirectTo: 'user', pathMatch: 'full' },
      {
        path: 'user',
        canActivate: [AuthGuard, permissionGuard('viewUserStatistics')],
        component: EmployeeStatisticsPageComponent,
      },
      {
        path: 'cooperative',
        canActivate: [AuthGuard, permissionGuard('viewExternalTenantStatistics')],
        component: TenantStatisticsPageComponent,
      },
      {
        path: 'maintenance-timer',
        canActivate: [AuthGuard, permissionGuard('viewMaintenanceTimerStatistics')],
        component: MaintenanceTimerStatisticsPageComponent,
      },
      {
        path: 'inspection-entry',
        canActivate: [AuthGuard, permissionGuard('viewInspectionEntryStatistics')],
        component: InspectionEntryStatisticPageComponent,
      },
      {
        path: 'defect-report',
        canActivate: [AuthGuard, permissionGuard('viewInspectionEntryStatistics')],
        component: DefectReportPageComponent,
      },
    ],
  },
  {
    path: 'landing',
    component: LandingPageComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'notifications',
    component: NotificationOverviewPageComponent,
  },
  {
    path: 'customFields',
    canActivate: [AuthGuard, permissionGuard('viewGenericForms')],
    children: [
      { path: '', redirectTo: 'fields', pathMatch: 'full' },
      { path: 'fields', component: CustomFieldSplitViewComponent, canDeactivate: [PendingChangesGuard] },
      { path: 'field-sets', component: CustomFieldCollectionSplitViewComponent, canDeactivate: [PendingChangesGuard] },
    ],
  },
  {
    path: 'protocolTypes',
    canActivate: [AuthGuard, permissionGuard('viewProtocolTypes')],
    children: [
      { path: '', component: ProtocolTypeOverviewPageComponent },
      { path: ':id', component: ProtocolTypeDetailPageComponent },
    ],
  },
  {
    path: 'protocols',
    canActivate: [AuthGuard, permissionGuard('viewProtocols')],
    children: [
      { path: '', component: ProtocolOverviewPageComponent },
      {
        path: 'new',
        component: ProtocolCreationPageComponent,
        canActivate: [permissionGuard('canCreateProtocols')],
        canDeactivate: [PendingChangesGuard],
      },
      {
        path: ':id',
        component: ProtocolDetailPageComponent,
        canDeactivate: [PendingChangesGuard],
      },
    ],
  },
  {
    path: 'settings',
    canActivate: [AuthGuard, permissionGuard('viewWeights')],
    children: [
      { path: '', redirectTo: 'defaults', pathMatch: 'full' },
      {
        path: 'defaults',
        component: SettingsPageComponent,
      },
    ],
  },
  {
    path: 'scan/:scanId',
    component: BuildingScanDetailPageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'pointCloudScan/:scanId',
    component: BuildingPointCloudScanPageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'shared/:id',
    component: ShareLinkPageComponent,
    children: [
      {
        path: 'scan',
        component: BuildingScanShareLinkPageComponent,
      },
      {
        path: 'pointCloud',
        component: BuildingPointCloudScanShareLinkPageComponent,
      },
    ],
  },
  {
    path: 'labels',
    component: LabelOverviewPageComponent,
    canActivate: [AuthGuard, permissionGuard('viewLabels')],
  },
  {
    path: 'label/:id',
    component: LabelDetailPageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'flat/:id',
    component: FlatDetailPageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: '**',
    redirectTo: 'landing',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  providers: [{ provide: LocationStrategy, useClass: PathLocationStrategy }],
  exports: [RouterModule],
})
export class AppRoutingModule {
}
